import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/de';

dayjs.extend(customParseFormat);
dayjs.locale('de');

/**
 * Custom DateAdapter for Angular Material Datepicker
 *
 * Extends NativeDateAdapter to support DD.MM.YYYY format parsing
 * from manual keyboard input.
 *
 * Features:
 * - Parses DD.MM.YYYY format (e.g., "01.01.2025")
 * - Validates date format strictly
 * - Returns null for invalid dates (triggers matDatepickerParse error)
 * - Falls back to native parsing for other formats
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  /**
   * Parse user input to Date object
   *
   * Handles manual keyboard input in DD.MM.YYYY format.
   * When parsing fails, returns null which triggers matDatepickerParse error.
   *
   * @param value - User input (string, number, or Date)
   * @returns Parsed Date object or null if invalid
   */
  override parse(value: string | number | Date): Date | null {
    // Handle Date objects
    if (value instanceof Date) {
      return value;
    }

    // Handle empty input
    if (!value) {
      return null;
    }

    // Handle string input with DD.MM.YYYY format
    if (typeof value === 'string') {
      const trimmed = value.trim();

      // For date picker manual input, we ONLY accept DD.MM.YYYY format
      // Reject anything that doesn't have dots (prevents "555" from being parsed)
      if (!trimmed.includes('.')) {
        // Allow ISO date strings from calendar selection
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          const timestamp = Date.parse(trimmed);
          return isNaN(timestamp) ? null : new Date(timestamp);
        }
        // Reject everything else that doesn't have dots
        return null;
      }

      // Additional validation: check format pattern
      const parts = trimmed.split('.');
      if (parts.length !== 3) {
        return null; // Not 3 parts separated by dots
      }

      const [day, month, year] = parts;

      // Check part lengths - must be exactly DD.MM.YYYY
      if (day.length !== 2 || month.length !== 2 || year.length !== 4) {
        return null; // Wrong length for DD.MM.YYYY
      }

      // Check if all parts are numeric
      if (!/^\d+$/.test(day) || !/^\d+$/.test(month) || !/^\d+$/.test(year)) {
        return null; // Contains non-numeric characters
      }

      // Try parsing with dayjs using strict mode
      const parsed = dayjs(trimmed, 'DD.MM.YYYY', true);

      if (parsed.isValid()) {
        // Verify the parsed date matches the input to catch overflow
        // (e.g., 55.55.5555 might get parsed but with different values)
        const parsedDay = parsed.format('DD');
        const parsedMonth = parsed.format('MM');
        const parsedYear = parsed.format('YYYY');

        if (parsedDay === day && parsedMonth === month && parsedYear === year) {
          // Return as native Date object
          return parsed.toDate();
        }
      }

      // Invalid DD.MM.YYYY format - return null to trigger error
      return null;
    }

    // Handle numeric timestamps
    if (typeof value === 'number') {
      return isNaN(value) ? null : new Date(value);
    }

    return null;
  }

  /**
   * Format Date object for display
   *
   * This uses the native implementation which respects the
   * Intl.DateTimeFormatOptions provided in main.ts
   */
  override format(date: Date, displayFormat: object): string {
    return super.format(date, displayFormat);
  }
}
